import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ContactInfo } from '../../components/contact-view/contact.view.component';
import { LanguageService } from '../language/language.service';
import {
  CmsStaticVariable,
  WebsiteCmsStaticVariablesService,
} from '../website-cms-static-variables/website-cms-static-variables.service';

export const MINISTRY_CONTACT_DEFAULT_INFO: ContactInfo = {
  title: 'צור קשר',
  phoneNumbers: ['*8787', '08-6831610'],
  serviceHours: {
    weekdays: "א' - ד': 5:30 בבוקר - 1:30 אחר חצות",
    weekend: "ה' - ו': 5:30 בבוקר ברצף עד יום שישי, שעה לפני כניסת שבת",
    saturdayNight: 'מוצאי שבת וחג: שעה לאחר צאת שבת / חג וברצף עד יום ראשון',
  },
  address: 'בניין ג\'נרי 2 (B), רחוב בנק ישראל 7, ת"ד 867, הקריה, ירושלים, מיקוד 9100801',
  email: 'pniotcrm@mot.gov.il',
  languages: ['עברית', 'ערבית', 'אנגלית', 'ורוסית'],
  publicReceptionLink: 'זמני קבלת קהל וכתובות',
  accessibilityCommitteeLink: 'פנייה לוועדת נגישות משרדית',
};

function getVar(variables: CmsStaticVariable[], key: string): string {
  const v = variables.find((x) => x.key === key);
  return v && v.value != null && v.value !== '' ? String(v.value).trim() : '';
}

/** Same mapping as mot-website-client HeaderComponent.buildContactInfoFromVariables. */
export function buildMinistryContactInfo(
  variables: CmsStaticVariable[],
  locale: string,
  base: ContactInfo
): ContactInfo {
  const g = (key: string) => getVar(variables, `${key}-${locale}`);
  const phoneShort = g('contacts-phone-short');
  const phoneFull = g('contacts-phone-full');
  const phoneNumbers = [phoneShort, phoneFull].filter(Boolean);
  const phoneLine1 = g('contacts-phone-line1');
  const phoneLine2 = g('contacts-phone-line2');
  const phoneLine3 = g('contacts-phone-line3');
  const hasApiHours = phoneLine1 || phoneLine2 || phoneLine3;
  const serviceHours = hasApiHours
    ? { weekdays: phoneLine1, weekend: phoneLine2, saturdayNight: phoneLine3 }
    : base.serviceHours;
  const serviceLangsLine1 = g('contacts-service-languages-line1');
  const languages = serviceLangsLine1
    ? serviceLangsLine1.split(/[,،\n]+/).map((s) => s.trim()).filter(Boolean)
    : [];
  return {
    ...base,
    phoneTitle: g('contacts-phone-title') || undefined,
    phoneNumbers: phoneNumbers.length > 0 ? phoneNumbers : base.phoneNumbers,
    serviceHours,
    addressTitle: g('contacts-address-title') || undefined,
    address: g('contacts-address-line1') || base.address,
    emailTitle: g('contacts-email-title') || undefined,
    email: g('contacts-email-address') || base.email,
    serviceLanguagesTitle: g('contacts-service-languages-title') || undefined,
    languages: languages.length > 0 ? languages : base.languages,
    publicReceptionLink:
      g('contacts-public-inquiries-link-name') || base.publicReceptionLink,
    publicReceptionUrl: g('contacts-public-inquiries-link-url') || undefined,
    accessibilityCommitteeLink:
      g('contacts-fine-appeals-link-name') || base.accessibilityCommitteeLink,
    complaintUrl: g('contacts-fine-appeals-link-url') || undefined,
    whatsappLink: g('contacts-whatsapp-link') || undefined,
    telegramLink: g('contacts-telegram-link') || undefined,
  };
}

/**
 * Shared ministry contact block from CMS static variables (one stream for nav-bar + mobile drawer).
 */
@Injectable({ providedIn: 'root' })
export class MinistryContactInfoService {
  private readonly _contactInfo = new BehaviorSubject<ContactInfo>(
    MINISTRY_CONTACT_DEFAULT_INFO
  );

  readonly contactInfo$ = this._contactInfo.asObservable();

  constructor(
    private readonly languageService: LanguageService,
    private readonly cms: WebsiteCmsStaticVariablesService
  ) {
    combineLatest([
      this.languageService.selectedLanguage$,
      this.cms.fetchStaticVariables(),
    ])
      .pipe(
        map(([lang, vars]) =>
          buildMinistryContactInfo(vars, lang.symbol, this._contactInfo.getValue())
        )
      )
      .subscribe((info) => this._contactInfo.next(info));
  }
}
